import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import FavouriteButton from "./FavouriteButton";
import { getFirestore, doc, getDoc } from "firebase/firestore";

const ADMIN_LOCK_DOC = "admin/warehouse_lock";
const LATEST_BACKUP_DOC = "admin/warehouse_latest_backup";

const StocktakeInstructions = () => {
  const db = getFirestore();
  const navigate = useNavigate();

  const [lockInfo, setLockInfo] = useState({ locked: false, reason: "", startedAt: "", endedAt: "" });
  const [latestBackup, setLatestBackup] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const lockSnap = await getDoc(doc(db, ADMIN_LOCK_DOC));
        if (lockSnap.exists()) setLockInfo(lockSnap.data());

        const latestSnap = await getDoc(doc(db, LATEST_BACKUP_DOC));
        if (latestSnap.exists()) setLatestBackup(latestSnap.data());
      } catch (e) {
        console.warn("Info fetch failed", e);
      }
    })();
  }, [db]);

  const Step = ({ num, title, children, actionText, route }) => (
    <section className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
      <header className="flex items-center gap-3 mb-3">
        <div className="flex items-center justify-center w-10 h-10 rounded-full bg-gray-800 text-white text-lg font-bold">
          {num}
        </div>
        <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
        {route && (
          <button
            onClick={() => navigate(route)}
            className="ml-auto px-3 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm"
            type="button"
          >
            {actionText || "Open"}
          </button>
        )}
      </header>
      <div className="text-sm text-gray-800 space-y-2">{children}</div>
    </section>
  );

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-4xl mx-auto p-6">
        <FavouriteButton pageUrl={window.location.pathname} />
        <h1 className="text-3xl font-semibold text-gray-900 mb-4">Stocktake — How To</h1>

        {/* Lock / Backup status */}
        <div className="mb-5 space-y-2">
          {lockInfo && lockInfo.locked ? (
            <div className="border-l-4 border-red-600 bg-red-50 text-red-900 px-4 py-3 rounded">
              <p className="font-semibold">Maintenance lock is ON.</p>
              <p className="text-sm">
                {lockInfo.reason ? `Reason: ${lockInfo.reason}. ` : ""}Started: {lockInfo.startedAt || "unknown"}
              </p>
            </div>
          ) : (
            <div className="border-l-4 border-emerald-600 bg-emerald-50 text-emerald-900 px-4 py-3 rounded">
              <p className="font-semibold">Maintenance lock is OFF.</p>
              <p className="text-sm">Allocations/edits may be happening in LIVE right now.</p>
            </div>
          )}

          {latestBackup && latestBackup.backupId && (
            <div className="border border-gray-200 bg-white px-4 py-2 rounded text-sm text-gray-700">
              Latest backup: <code>{latestBackup.backupId}</code>{" "}
              {latestBackup.at ? `@ ${latestBackup.at}` : ""}{" "}
              {Number.isFinite(latestBackup.count) ? `• ${latestBackup.count} docs` : ""}
            </div>
          )}
        </div>

        {/* Pre-flight checks */}
        <div className="bg-yellow-50 border border-yellow-300 text-yellow-900 px-4 py-3 rounded mb-6 text-sm">
          <p className="font-medium">Pre-flight (don’t skip):</p>
          <ul className="list-disc ml-5 mt-1 space-y-1">
            <li>Stable network on the handheld. If it drops, use <em>Sync &amp; Verify</em> later.</li>
            <li>Scans <strong>may duplicate</strong></li>
            <li>Allocation is preserved from LIVE by key: <code>SerialNumber + ProductId + Special</code>.</li>
          </ul>
        </div>

        <div className="grid gap-5">
          <Step num={1} title="Scan stock (goes to stocktake_scanned)" actionText="Open scanner" route="/StockTakeForm">
            <ul className="list-disc ml-5">
              <li>Scan ITEM barcode (first), then LOCATION barcode.</li>
              <li>Each scan writes to <code>stocktake_scanned</code> immediately and also saves locally.</li>
              <li>Duplicates are expected. Don’t tidy them — we handle counts later.</li>
              <li>Use <em>Sync &amp; Verify</em> if you were offline; you should see “verified”.</li>
            </ul>
          </Step>

          <Step num={2} title="Review scans & export" actionText="Open review" route="/StockComparison">
            <ul className="list-disc ml-5">
              <li>Check the list looks sane. Use search to spot obvious nonsense.</li>
              <li>Export Excel. You’ll get two sheets: <strong>Raw Scans</strong> and <strong>Grouped</strong> (S+P+Spec+Loc).</li>
              <li>If counts are off, go back to step 1 and finish scanning — do <em>not</em> shortcut here.</li>
            </ul>
          </Step>

          <Step num={3} title="Transfer scans → Duplicate (preserves allocations from LIVE)" actionText="Run transfer" route="/StocktakeToDuplicate">
            <ul className="list-disc ml-5">
              <li>Duplicate is fully rebuilt from scans; no writes to LIVE.</li>
              <li>
                Allocation carry-over: if LIVE has <code>K</code> allocated for the same{" "}
                <code>Serial+Product+Special</code> and scans have <code>N</code>, we set <code>min(K, N)</code> allocated in duplicate.
              </li>
              <li><code>Stock</code> = <code>Special === "50"</code>. Location comes from the scan.</li>
            </ul>
          </Step>

          <Step num={4} title="Review Duplicate Warehouse (staging)" actionText="Open duplicate" route="/DuplicateWarehouse">
            <ul className="list-disc ml-5">
              <li>Banner reminds you this view is <strong>not live</strong>.</li>
              <li>Spot-check product counts, locations, and allocated flags.</li>
              <li>If wrong, re-run step 3; don’t manually hack the duplicate.</li>
            </ul>
          </Step>

          <Step num={5} title="Publish Duplicate → Live (with backup & rollback)" actionText="Open publisher" route="/PublishDuplicateToLive">
            <ul className="list-disc ml-5">
              <li>Takes a snapshot of LIVE to <code>cylinders_warehouse_backups/&lt;id&gt;/docs</code>.</li>
              <li>Clears LIVE, then copies from duplicate. Latest backup pointer is updated.</li>
              <li>Use the lock during publish to avoid races; unlock after success.</li>
              <li>Rollback restores LIVE from the latest backup if needed.</li>
            </ul>
          </Step>
        </div>

        <div className="mt-6 bg-white border border-gray-200 rounded-xl p-4 text-sm">
          <p className="font-semibold mb-2">Gotchas:</p>
          <ul className="list-disc ml-5 space-y-1">
            <li>Missing product codes in exports? Populate <code>cyl_info</code>.</li>
            <li>Allocated counts look off? Check LIVE first — we mirror LIVE’s allocation totals per key.</li>
            <li>Publishing failed mid-way? Use <strong>Rollback</strong>, fix the issue, retry publish.</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default StocktakeInstructions;
